import { useEffect, useSyncExternalStore } from 'react'
import { Button, Modal } from '../ui'
import { useInstallPrompt } from '../../hooks/useInstallPrompt'
import { getInstallGuide } from '../../lib/installGuide'
import {
  getDeferredInstallPrompt,
  promptDeferredInstall,
  startInstallPromptCapture,
  subscribeInstallPrompt,
} from '../../lib/pwaInstall'

/**
 * Bottom sheet offering "add to home screen", shown once, after the first
 * wear log. Renders nothing until that trigger fires.
 *
 * Two paths because the platforms are not the same:
 *  - native      → Chrome/Edge/Android: we hold a real prompt event, one tap.
 *  - ios-manual  → Safari has no install API. The user must do it by hand, so
 *                  the only useful thing we can do is show them where.
 */
export default function InstallPrompt() {
  const { visible, mode, promptInstall, dismiss } = useInstallPrompt()

  if (!visible) return null

  return (
    <div
      className="fixed left-0 right-0 z-50 px-4 animate-slide-up"
      style={{ bottom: 'calc(4rem + env(safe-area-inset-bottom, 0px) + 0.75rem)' }}
      role="dialog"
      aria-label="Add FragranceOS to your home screen"
    >
      <div className="mx-auto max-w-md rounded-2xl border border-gold/25 bg-charcoal-800/98 backdrop-blur px-5 py-4 shadow-2xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-body text-base text-gold leading-tight">
              Keep it on your home screen
            </h2>
            <p className="mt-1 text-sm text-cream/70 leading-snug">
              {mode === 'ios-manual'
                ? 'Opens like an app — and it’s the only way FragranceOS can remind you to log tomorrow.'
                : 'Opens like an app, works offline, and can remind you to log tomorrow.'}
            </p>
          </div>
          <button
            onClick={dismiss}
            aria-label="Dismiss"
            className="-mt-1 -mr-1 h-8 w-8 shrink-0 text-xl leading-none text-cream/35 hover:text-cream/80 transition-colors"
          >
            ×
          </button>
        </div>

        {mode === 'ios-manual' ? (
          <>
            <ol className="mt-3 flex flex-col gap-2 text-sm text-cream/75">
              <li className="flex items-center gap-2.5">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gold/15 text-[11px] font-semibold text-gold">1</span>
                <span>Tap <ShareGlyph /> in the Safari toolbar</span>
              </li>
              <li className="flex items-center gap-2.5">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gold/15 text-[11px] font-semibold text-gold">2</span>
                <span>Choose <span className="text-cream">Add to Home Screen</span></span>
              </li>
            </ol>
            <Button variant="ghost" onClick={dismiss} className="mt-4 w-full">
              Got it
            </Button>
          </>
        ) : (
          <div className="mt-4 flex gap-3">
            <Button variant="ghost" onClick={dismiss} className="flex-1">
              Not now
            </Button>
            <Button onClick={promptInstall} className="flex-1">
              Add to Home Screen
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

/** iOS share icon — drawn inline so the instruction matches what they see. */
function ShareGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-label="Share"
      role="img"
      className="inline-block h-4 w-4 -translate-y-px text-gold"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 15V3" />
      <path d="M8 7l4-4 4 4" />
      <path d="M6 12v7a2 2 0 002 2h8a2 2 0 002-2v-7" />
    </svg>
  )
}

/**
 * On-demand install guide, opened from Profile and from the first-visit
 * banner. Built on the shared Modal so focus moves in, is trapped, Escape
 * closes it and focus returns to whatever opened it.
 *
 * Shows platform-specific numbered steps. Never auto-dismisses — the user
 * keeps it open until they are done reading.
 */
export function InstallGuideSheet({ open, onClose }) {
  useEffect(() => { startInstallPromptCapture() }, [])
  const deferredPrompt = useSyncExternalStore(subscribeInstallPrompt, getDeferredInstallPrompt, () => null)
  const guide = open ? getInstallGuide(navigator.userAgent, deferredPrompt != null) : null
  const appUrl = typeof window === 'undefined' ? '' : `${window.location.origin}/`

  const handleInstall = async () => {
    try {
      await promptDeferredInstall()
    } catch {
      // The browser refused the prompt; nothing else to offer from here.
    }
    onClose()
  }

  const copyAppLink = () => {
    try {
      navigator.clipboard?.writeText(appUrl)?.catch?.(() => {})
    } catch {
      // Clipboard API unavailable; the link above still works by long-press.
    }
  }

  return (
    <Modal
      open={open && Boolean(guide)}
      onClose={onClose}
      title={guide?.platform === 'standalone' ? 'Already installed' : 'Get the app on your phone'}
      maxWidth="max-w-md"
    >
      {guide && (
        <div data-install-guide-sheet data-install-guide-platform={guide.platform}>
          {guide.platform === 'standalone' ? (
            <p className="text-sm text-cream/70">{guide.steps[0]}</p>
          ) : (
            <>
              <ol className="flex flex-col gap-2.5 text-sm text-cream/75">
                {guide.steps.map((step, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gold/15 text-[11px] font-semibold text-gold">
                      {i + 1}
                    </span>
                    <span className="flex-1">{step}</span>
                  </li>
                ))}
              </ol>

              {guide.copyLink && (
                <>
                  {/* A real link: the steps tell the user to tap and hold it. */}
                  <a
                    href={appUrl}
                    data-install-guide-link
                    className="mt-4 block break-all rounded-lg border border-gold/25 px-3 py-3 text-sm text-gold underline underline-offset-2"
                  >
                    {appUrl}
                  </a>
                  <Button variant="ghost" onClick={copyAppLink} className="mt-2 w-full">
                    Copy link
                  </Button>
                </>
              )}

              {guide.hasNativePrompt && (
                <Button onClick={handleInstall} className="mt-4 w-full">
                  Add to Home Screen
                </Button>
              )}
            </>
          )}

          {!guide.hasNativePrompt && !guide.copyLink && guide.platform !== 'standalone' && (
            <Button variant="ghost" onClick={onClose} className="mt-4 w-full">
              Got it
            </Button>
          )}
        </div>
      )}
    </Modal>
  )
}
